import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DailyTaskRow, useDailyTaskQuery } from '@features/DailyTask';
// Не из индексов фич: они тянут экраны. Здесь — только данные для счётчика жетонов дня.
import { useGrammarReviewQuery } from '@features/Grammar/model/queries';
import { useStoriesHubQuery } from '@features/Stories/model/queries';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import { TokenIcon } from '@shared/ui';
import { GrammarRow } from './GrammarRow';
import { MissionRow } from './MissionRow';
import { PlanHintRow } from './PlanHintRow';
import { ReviewRow } from './ReviewRow';

/**
 * «На сегодня» (владелец 2026-11-03: «Список дня», затем макет 3 — игра своей карточкой под тренировкой): строками одной
 * плашки «Задание дня», «Миссия дня», подборка повторения грамматики и грамматика до награды; «новое слово» — ссылкой
 * в тренировке (макет 4). Строка, чьих данных нет или которой сегодня нет, не рисуется.
 */
export function DailiesCard({ phone }: { phone: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Жетоны дня: сколько уже получено из того, что можно получить сегодня (задание, миссия, подборка повтора).
  const task = useDailyTaskQuery().data;
  const hub = useStoriesHubQuery().data;
  const review = useGrammarReviewQuery().data;
  let can = 0;
  let got = 0;
  if (task && task.status !== 'empty') {
    can += task.status === 'done' ? task.reward : task.rewardTokens;
    if (task.status === 'done') got += task.reward;
  }
  if (hub) {
    can += hub.daily.granted ?? hub.daily.reward.perfect;
    got += hub.daily.granted ?? 0;
  }
  if (review && (review.available || review.done_today)) {
    can += review.reward;
    if (review.granted) got += review.reward;
  }
  return (
    <section
      aria-labelledby="dash-dailies-title"
      // Сверху на широкой колонке — 28 px, как у тренировки: надзаголовки на одной линии (DashboardPage.css).
      className={cn('dash-dailies flex flex-col rounded-card border border-solid border-accent/25 bg-card', phone ? 'p-4' : 'px-6 py-5')}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 id="dash-dailies-title" className="text-[12px] font-semibold uppercase leading-4 tracking-[0.14em] text-muted">
          {t('dashboard.home.dailies.title')}
        </h2>
        {can > 0 ? (
          <span className="inline-flex items-center gap-2 text-[13px] font-semibold text-fg" aria-label={t('dashboard.home.dailies.tokens', { got, can })}>
            <TokenIcon size={16} />
            <span className="whitespace-nowrap tabular-nums">{t('dashboard.home.dailies.tokens', { got, can })}</span>
            <span aria-hidden className="dl-tokens-bar relative block h-1.5 w-14 overflow-hidden rounded-full bg-white/10">
              <i className="absolute inset-y-0 left-0 block rounded-full bg-accent" style={{ width: `${Math.round((got / can) * 100)}%` }} />
            </span>
          </span>
        ) : null}
      </div>
      {/* Строки подряд: 12 px, черта, 12 px — плашка по своим строкам, не по высоте соседки (владелец 2026-10-26). */}
      <div className="flex flex-col divide-y divide-line [&>*:first-child]:pb-3 [&>*:last-child]:pt-3 [&>*:not(:first-child):not(:last-child)]:py-3 [&>*:only-child]:py-0">
        <DailyTaskRow onOpen={() => navigate(paths.dailyTask)} />
        <MissionRow />
        <ReviewRow />
        <GrammarRow />
        <PlanHintRow />
      </div>
    </section>
  );
}
